import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Building2, Landmark, Wallet } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { money, formatDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { PartnerFields } from "@/components/PartnerFields";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export default function PartnerDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => {
    try { const { data } = await api.get(`/partners/${id}`); setP(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [id]);

  const save = async () => {
    const out = {}; Object.entries(form).forEach(([k, v]) => { out[k] = v === "" ? null : v; });
    out.company_name = form.company_name || "Azienda";
    try { const { data } = await api.put(`/partners/${id}`, out); setP(data); setOpen(false); toast.success("Salvato"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!p) return <div className="text-slate-400 py-10">{t("common.loading")}</div>;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="partner-detail-page">
      <button onClick={() => navigate("/partners")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft size={16} /> {t("common.back")}</button>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display text-2xl lg:text-3xl font-bold text-slate-900">{p.company_name}</h1>
            <StatusBadge status={p.payment_status || "pending"} />
          </div>
          <p className="text-sm text-slate-500 mt-1">{p.contact_name} · {p.email}</p>
        </div>
        <Button onClick={() => { setForm(p); setOpen(true); }} className="bg-blue-900 hover:bg-blue-800" data-testid="partner-edit-detail-btn"><Pencil size={16} className="mr-1.5" /> {t("common.edit")}</Button>
      </div>

      {/* Payment KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Totale maturato" value={money(p.total_earned)} />
        <Kpi label="Totale pagato" value={money(p.total_paid)} />
        <Kpi label="Da pagare" value={money(p.total_pending)} accent="text-amber-700" />
        <Kpi label="Ultimo pagamento" value={p.last_payment_date || "—"} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card icon={Building2} title="Dati azienda">
          <Row k="Ragione sociale" v={p.company_name} />
          <Row k="Partita IVA" v={p.vat_number} />
          <Row k="Codice fiscale" v={p.tax_code} />
          <Row k="N. registro imprese" v={p.registration_number} />
          <Row k="Referente" v={p.contact_name} />
          <Row k="Telefono" v={p.phone} />
          <Row k="Email" v={p.email} />
          <Row k="Indirizzo" v={p.address} />
          <Row k="Città / CAP" v={[p.city, p.postal_code].filter(Boolean).join(" ")} />
          <Row k="Paese" v={p.country} />
        </Card>
        <div className="space-y-6">
          <Card icon={Landmark} title="Coordinate bancarie">
            <Row k="Banca" v={p.bank_name} />
            <Row k="Intestatario" v={p.account_holder} />
            <Row k="IBAN" v={p.iban} mono />
            <Row k="SWIFT/BIC" v={p.swift} mono />
            <Row k="Paese banca" v={p.bank_country} />
          </Card>
          <Card icon={Wallet} title="Dettagli pagamento">
            <Row k="Metodo preferito" v={p.payment_method} />
            <Row k="Termini" v={p.payment_terms} />
            <Row k="Tariffa interna" v={p.internal_rate != null ? `${p.internal_rate}%` : "—"} />
            <Row k="Commissione" v={p.commission_pct != null ? `${p.commission_pct}%` : "—"} />
            <Row k="Accordo commissioni" v={p.commission_agreement} />
            <Row k="Stato" v={p.payment_status} />
          </Card>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Modifica azienda partner</DialogTitle></DialogHeader>
          <PartnerFields form={form} setForm={setForm} adminMode={true} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={save} className="bg-blue-900 hover:bg-blue-800" data-testid="partner-detail-save-btn">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
      <div className="text-xs uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`font-display text-xl lg:text-2xl font-bold mt-1 ${accent || "text-slate-900"}`}>{value}</div>
    </div>
  );
}
function Card({ icon: Icon, title, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
      <h3 className="font-display text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2"><Icon size={18} className="text-blue-900" strokeWidth={1.5} /> {title}</h3>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}
function Row({ k, v, mono }) {
  return (
    <div className="flex items-center justify-between gap-4 text-sm">
      <span className="text-slate-400">{k}</span>
      <span className={`text-slate-800 font-medium text-right ${mono ? "font-mono text-xs" : ""}`}>{v || "—"}</span>
    </div>
  );
}
